import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopHeader } from '../../components';
import { ProfileTab } from '../../components/settings/ProfileTab';
import { SecurityTab } from '../../components/settings/SecurityTab';
import { AppearanceTab } from '../../components/settings/AppearanceTab';
import { CollaboratorsTab } from '../../components/settings/CollaboratorsTab';
import { NotificationsTab } from '../../components/settings/NotificationsTab';

type TabId = 'profile' | 'security' | 'appearance' | 'collaborators' | 'notifications';

const tabs: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'collaborators', label: 'Collaborators' },
  { id: 'notifications', label: 'Notifications' },
];

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(tabFromUrl || 'profile');

  // Update URL when tab changes
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync state with URL changes (back/forward navigation)
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader title="Settings" />

      {/* Tab Navigation */}
      <div className="border-b border-border bg-background-card">
        <nav className="flex gap-1 md:gap-2 -mb-px overflow-x-auto px-4 md:px-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-4 md:p-8">
        {activeTab === 'profile' && <ProfileTab />}
        {activeTab === 'security' && <SecurityTab />}
        {activeTab === 'appearance' && <AppearanceTab />}
        {activeTab === 'collaborators' && <CollaboratorsTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
      </div>
    </div>
  );
}
